import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop";
import { Modal } from "../ui/Modal";
import {
  THUMBNAIL_MAX_BYTES,
  THUMBNAIL_MIN_HEIGHT,
  THUMBNAIL_MIN_WIDTH,
} from "@vedaunion/shared";

/**
 * Forced-16:9 thumbnail cropper. Loads the source image, lets the editor
 * drag + zoom to choose the crop window, then exports a JPEG via canvas.
 *
 * Output budget: ≤ 2 MB (YouTube's `thumbnails/set` cap). We try
 * progressively lower JPEG qualities (0.92 → 0.88 → … → 0.5) until the
 * blob fits. If it can't fit even at quality 0.5, we surface a warning to
 * the editor and let them upload anyway.
 *
 * Input validation: the source image must be at least 1280×720 (so the
 * 16:9 crop has enough resolution to look crisp on YouTube). We block at
 * load-time before opening the cropper.
 */

interface Props {
  /** Source image — File from a picker or a Blob. */
  source: File | Blob;
  open: boolean;
  onClose: () => void;
  /** Called when the editor accepts the crop. The Blob is JPEG and ≤ 2 MB. */
  onAccept: (cropped: Blob) => void;
}

const QUALITY_STEPS = [0.92, 0.88, 0.82, 0.75, 0.68, 0.6, 0.5];

export function ThumbnailCropper({ source, open, onClose, onAccept }: Props) {
  const [imageUrl] = useState(() => URL.createObjectURL(source));
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<{
    w: number;
    h: number;
  } | null>(null);

  const onCropComplete = useCallback((_a: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const onMediaLoaded = useCallback(
    (mediaSize: { naturalWidth: number; naturalHeight: number }) => {
      setNaturalSize({
        w: mediaSize.naturalWidth,
        h: mediaSize.naturalHeight,
      });
      if (
        mediaSize.naturalWidth < THUMBNAIL_MIN_WIDTH ||
        mediaSize.naturalHeight < THUMBNAIL_MIN_HEIGHT
      ) {
        setError(
          `Thumbnails need to start at least ${THUMBNAIL_MIN_WIDTH}×${THUMBNAIL_MIN_HEIGHT}. This image is ${mediaSize.naturalWidth}×${mediaSize.naturalHeight} and would look soft on YouTube.`,
        );
      }
    },
    [],
  );

  const handleAccept = useCallback(async () => {
    if (!croppedAreaPixels) return;
    setExporting(true);
    setError(null);
    try {
      const blob = await renderCropToJpeg(imageUrl, croppedAreaPixels);
      onAccept(blob);
      URL.revokeObjectURL(imageUrl);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't export the cropped image.");
    } finally {
      setExporting(false);
    }
  }, [croppedAreaPixels, imageUrl, onAccept, onClose]);

  return (
    <Modal
      open={open}
      onClose={() => {
        URL.revokeObjectURL(imageUrl);
        onClose();
      }}
      title="Choose a thumbnail frame"
      eyebrow="Thumbnail · 16:9"
      size="lg"
    >
      <div className="yt-cropper">
        <div className="yt-cropper-stage">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={16 / 9}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onMediaLoaded={onMediaLoaded}
            objectFit="contain"
            showGrid={true}
          />
        </div>
        <div className="yt-cropper-controls">
          <label className="yt-cropper-zoom">
            <span className="font-grotesk text-[0.62rem] uppercase tracking-[0.18em] text-[var(--color-ink-mute)]">
              Zoom
            </span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              aria-label="Zoom"
            />
          </label>
          {naturalSize && (
            <p className="font-grotesk text-[0.7rem] text-[var(--color-ink-mute)]">
              Source: {naturalSize.w}×{naturalSize.h}
            </p>
          )}
        </div>
        <p className="yt-cropper-hint font-display-italic">
          Drag to reposition. Scroll or pinch to zoom. The 16:9 frame is what
          YouTube will show.
        </p>
        {error && (
          <p className="yt-admin-error" role="alert">
            {error}
          </p>
        )}
        <div className="yt-cropper-actions">
          <button
            type="button"
            className="btn-app-ghost"
            onClick={() => {
              URL.revokeObjectURL(imageUrl);
              onClose();
            }}
            disabled={exporting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-app"
            onClick={handleAccept}
            disabled={exporting || !croppedAreaPixels || !!error}
          >
            {exporting ? "Exporting…" : "Use this frame"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Read the image, draw the cropped region to a canvas, encode as JPEG with
 * progressively lower quality until ≤ 2 MB. Returns the Blob.
 */
async function renderCropToJpeg(
  imageUrl: string,
  area: Area,
): Promise<Blob> {
  const img = await loadImage(imageUrl);
  // Cap the output at 1920×1080. YouTube only stores ≤1280×720 anyway,
  // but going slightly above gives a softer downscale on Google's side.
  const targetWidth = Math.min(1920, area.width);
  const targetHeight = Math.round((targetWidth / 16) * 9);
  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context not available");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    targetWidth,
    targetHeight,
  );
  for (const q of QUALITY_STEPS) {
    const blob = await canvasToBlob(canvas, "image/jpeg", q);
    if (blob.size <= THUMBNAIL_MAX_BYTES) return blob;
  }
  // Even at quality 0.5 it's too big — return the smallest we managed and
  // let the server surface the size error if needed.
  return canvasToBlob(canvas, "image/jpeg", 0.5);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = url;
  });
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob failed"))),
      type,
      quality,
    );
  });
}
